import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Link,
  Button,
  Box,
  TextField,
  Stack,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import {
  Instagram,
  Facebook,
  LinkedIn,
  Twitter,
  Email,
  Phone,
  LocationOn,
} from "@mui/icons-material";
import logo from "../../assets/images/logo.svg";
import video from "../../assets/videos/logo.mp4";

const SOCIAL_LINKS = [
  {
    icon: Facebook,
    url: "https://facebook.com/jeremysolutions",
    label: "Facebook",
  },
  {
    icon: Instagram,
    url: "https://instagram.com/jeremysolutions",
    label: "Instagram",
  },
  {
    icon: LinkedIn,
    url: "https://linkedin.com/company/jeremysolutions",
    label: "LinkedIn",
  },
  {
    icon: Twitter,
    url: "https://twitter.com/jeremysolutions",
    label: "Twitter",
  },
];

const CONTACT_INFO = [
  {
    icon: Email,
    text: "info@jeremysolutions.co.ke",
    url: "mailto:info@jeremysolutions.co.ke",
  },
  { icon: Phone, text: "+254 712 345 678", url: "tel:+254712345678" },
  { icon: LocationOn, text: "Nairobi, Kenya", url: "https://goo.gl/maps/..." },
];

export default function Footer() {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch("/api/newsletters/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Email: email }),
      });

      if (response.ok) {
        setSuccess("Thank you for subscribing!");
        setEmail("");
      } else {
        throw new Error("Failed to subscribe");
      }
    } catch (err) {
      setError("Failed to subscribe, please try again later.");
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSuccess("");
        setError("");
      }, 3000);
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "primary.main",
        color: "common.white",
        pt: { xs: 6, md: 8 },
        pb: 4,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          background: `linear-gradient(to bottom, ${alpha(
            theme.palette.common.black,
            0.1
          )}, transparent)`,
        },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Box
                sx={{
                  width: { xs: "100%", md: 200 },
                  height: "auto",
                }}
              >
                <video
                  src={video}
                  alt="Jeremy Solutions"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                  }}
                  autoPlay
                  loop
                  muted
                />
              </Box>
              <Stack direction="row" spacing={2}>
                {SOCIAL_LINKS.map(({ icon: Icon, url, label }) => (
                  <IconButton
                    key={label}
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "common.white",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.common.white, 0.1),
                        transform: "translateY(-4px)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <Icon />
                  </IconButton>
                ))}
              </Stack>
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Quick Links
            </Typography>
            <Stack spacing={1.5}>
              {["Home", "About", "Services", "Contact"].map((item) => (
                <Link
                  key={item}
                  href={`/${item.toLowerCase()}`}
                  underline="none"
                  color="inherit"
                  sx={{
                    opacity: 0.8,
                    "&:hover": {
                      opacity: 1,
                      color: "secondary.main",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {item}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Services
            </Typography>
            <Stack spacing={1.5}>
              {[
                "Web Design",
                "GIS Services",
                "Digital Marketing",
                "Art Sales",
              ].map((service) => (
                <Link
                  key={service}
                  href={`/${service.toLowerCase().replace(" ", "-")}`}
                  underline="none"
                  color="inherit"
                  sx={{
                    opacity: 0.8,
                    "&:hover": {
                      opacity: 1,
                      color: "secondary.main",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {service}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Newsletter Section */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Stay Updated
            </Typography>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Subscribe to our newsletter for updates and exclusive offers.
              </Typography>
              <form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={{
                      bgcolor: alpha(theme.palette.common.white, 0.1),
                      borderRadius: 1,
                      "& .MuiOutlinedInput-root": {
                        color: "common.white",
                        "& fieldset": {
                          borderColor: alpha(theme.palette.common.white, 0.2),
                        },
                        "&:hover fieldset": {
                          borderColor: alpha(theme.palette.common.white, 0.3),
                        },
                      },
                      "& input::placeholder": {
                        color: alpha(theme.palette.common.white, 0.5),
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : "Subscribe"}
                  </Button>
                </Stack>
              </form>
              {(error || success) && (
                <Alert severity={error ? "error" : "success"}>
                  {error || success}
                </Alert>
              )}
            </Stack>
          </Grid>
        </Grid>

        <Divider
          sx={{ my: 4, borderColor: alpha(theme.palette.common.white, 0.1) }}
        />

        {/* Bottom Section */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ opacity: 0.8 }}
        >
          <Typography variant="body2">
            © {new Date().getFullYear()} Jeremy Solutions. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Link
              href="/privacy"
              underline="none"
              color="inherit"
              sx={{ "&:hover": { color: "secondary.main" } }}
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              underline="none"
              color="inherit"
              sx={{ "&:hover": { color: "secondary.main" } }}
            >
              Terms of Service
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
